import { f7 } from 'framework7-vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/authStore';
import { USER_STATUS } from '@/utils/userStatus';

// Temporary fixed suspension-until date until the backend exposes a real value
// on the user resource. Update or remove once a real `suspendedUntil` (or
// equivalent) attribute ships.
export const SUSPENDED_UNTIL_ISO = '2026-08-01';

const DATE_LABELS = {
  ar: '1 أغسطس 2026',
  en: '1 Aug 2026',
};

const DEFAULT_BLOCKED_KEY = 'accountGate.blocked.default';

// Keyed by user id — auto-resets when authStore.user.id changes (logout, switch).
let bannerOpenedForUserId = null;
let lastToastAt = 0;
const TOAST_THROTTLE_MS = 1500;

export function useAccountGate() {
  const authStore = useAuthStore();
  const { t, te, locale } = useI18n({ useScope: 'global' });

  const isSuspended = computed(
    () => authStore.user?.status === USER_STATUS.SUSPENDED,
  );

  const suspendedUntilLabel = computed(
    () => DATE_LABELS[locale.value] ?? DATE_LABELS.en,
  );

  /**
   * Gate an action. Returns true when the user may proceed.
   * When blocked, shows a localized toast (unless `silent: true`) and returns
   * false so callers can `if (!guard(...)) return;` cleanly.
   */
  function guard(actionKey, { silent = false } = {}) {
    if (!isSuspended.value) return true;
    if (silent) return false;

    const now = Date.now();
    if (now - lastToastAt < TOAST_THROTTLE_MS) return false;
    lastToastAt = now;

    const specificKey = `accountGate.blocked.${actionKey}`;
    const key = te(specificKey) ? specificKey : DEFAULT_BLOCKED_KEY;

    f7?.toast?.create({
      text: t(key, { date: suspendedUntilLabel.value }),
      position: 'center',
      closeTimeout: 3000,
      icon: '<i class="f7-icons">lock_fill</i>',
    }).open();

    return false;
  }

  /**
   * Open a sticky F7 notification once per user-session when the user is
   * suspended. Auto-resets when the active user id changes (logout, switch).
   */
  function openSuspensionBanner() {
    const uid = authStore.user?.id ?? null;
    if (!isSuspended.value || bannerOpenedForUserId === uid) return;
    if (!f7?.notification) return;

    bannerOpenedForUserId = uid;

    f7.notification.create({
      icon: '<i class="f7-icons" style="color:#f59e0b">exclamationmark_triangle_fill</i>',
      title: t('accountGate.banner.title'),
      subtitle: t('accountGate.banner.subtitle', { date: suspendedUntilLabel.value }),
      closeButton: true,
      closeOnClick: false,
    }).open();
  }

  return {
    isSuspended,
    suspendedUntilLabel,
    guard,
    openSuspensionBanner,
  };
}
